import * as React from 'react';
import { createContext, PropsWithChildren, useContext, useReducer } from 'react';

type TBannersState = {
  messageId: string;
  actionId: string;
  logoId: string;
  linkId: string;
  layoutId: string;
  imageId: string;
};

const defaultState = {
  actionId: 'start-journey',
  logoId: 'us-expedia',
  layoutId: 'medium-rectangle',
  linkId: 'home',
  imageId: 'sailing',
  messageId: 'bye-bye-bucket-list-hello-adventure',
};

const initialState: TBannersState = {
  actionId: defaultState.actionId,
  logoId: defaultState.logoId,
  layoutId: undefined, // empty initially to emit resize-message once instead of twice on the first SET_LAYOUT
  linkId: defaultState.linkId,
  imageId: defaultState.imageId,
  messageId: defaultState.messageId,
};

const actions = {
  SET_ACTION: 'SET_ACTION',
  SET_IMAGE: 'SET_IMAGE',
  SET_LAYOUT: 'SET_LAYOUT',
  SET_LINK: 'SET_LINK',
  SET_LOGO: 'SET_LOGO',
  SET_MESSAGE: 'SET_MESSAGE',
};

function reducer(state: TBannersState, action): TBannersState {
  switch (action.type) {
    case actions.SET_ACTION: {
      return { ...state, actionId: action.value ?? defaultState.actionId };
    }
    case actions.SET_IMAGE: {
      return { ...state, imageId: action.value ?? defaultState.imageId };
    }
    case actions.SET_LAYOUT: {
      return { ...state, layoutId: action.value ?? defaultState.layoutId };
    }
    case actions.SET_LINK: {
      return { ...state, linkId: action.value ?? defaultState.linkId };
    }
    case actions.SET_LOGO: {
      return { ...state, logoId: action.value ?? defaultState.logoId };
    }
    case actions.SET_MESSAGE: {
      return { ...state, messageId: action.value ?? defaultState.messageId };
    }
    default: {
      return state;
    }
  }
}

type TBannersContext = {
  setAction(value): void;
  setImage(value): void;
  setLayout(value): void;
  setLink(value): void;
  setLogo(value): void;
  setMessage(value): void;
} & TBannersState;

const BannersContext = createContext<TBannersContext>({
  ...initialState,
  setAction: undefined,
  setImage: undefined,
  setLayout: undefined,
  setLink: undefined,
  setLogo: undefined,
  setMessage: undefined,
});

function BannersProvider({ children }: PropsWithChildren<unknown>): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = {
    ...state,
    setAction(value): void {
      dispatch({ type: actions.SET_ACTION, value });
    },
    setImage(value): void {
      dispatch({ type: actions.SET_IMAGE, value });
    },
    setLayout(value): void {
      dispatch({ type: actions.SET_LAYOUT, value });
    },
    setLink(value): void {
      dispatch({ type: actions.SET_LINK, value });
    },
    setLogo(value): void {
      dispatch({ type: actions.SET_LOGO, value });
    },
    setMessage(value): void {
      dispatch({ type: actions.SET_MESSAGE, value });
    },
  };

  return <BannersContext.Provider value={value}>{children}</BannersContext.Provider>;
}

function useBannersContext(): TBannersContext {
  return useContext(BannersContext);
}

export { BannersContext, BannersProvider, useBannersContext };
